import { Card } from "@design";
import { CalendarDays } from "lucide-react";
import { Text } from "@/components/Text";
import { busyItems } from "./calendarItems";
import { type CalendarWeek, daysIn, hourRange } from "./calendarModel";
import { TimeGrid } from "./TimeGrid";
import { useCalendarText } from "./useCalendarText";

/**
 * How someone's week looks (view_schedule, MHS-969): their busy time on a small week, with how much of their working
 * time is free, their longest free stretch and their busiest day. Meeting titles show where their sharing allows.
 */
export function CalendarWeekCard({ widget }: Readonly<{ widget: CalendarWeek }>) {
  const text = useCalendarText();
  const { t } = text;
  const { person, summary } = widget;
  const days = daysIn(
    widget.window,
    person.hours,
    person.busy.map((b) => b.start.slice(0, 10)),
  );
  return (
    <Card>
      <section aria-label={t("week.label", { person: person.email })} className="space-y-3">
        <div className="space-y-1">
          <Text variant="caption" tone="tertiary" className="inline-flex items-center gap-1.5">
            <CalendarDays size={14} aria-hidden="true" />
            {t("week.eyebrow")}
          </Text>
          <Text variant="h3" className="break-words">
            {person.email}
          </Text>
        </div>
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Fact
            term={t("week.free")}
            value={t("week.freeValue", {
              free: text.hours(summary.freeMinutes),
              working: text.hours(summary.workingMinutes),
            })}
          />
          {summary.longestFree && (
            <Fact term={t("week.longest")} value={text.when(summary.longestFree.start, summary.longestFree.end)} />
          )}
          {summary.busiestDay && <Fact term={t("week.busiest")} value={text.day(summary.busiestDay, true)} />}
        </dl>
        <TimeGrid
          days={days}
          range={hourRange(person.hours, [])}
          items={busyItems([person], days, (p, b) => text.block(p, b), { labels: true })}
          label={t("week.label", { person: person.email })}
          rowHeight={26}
        />
      </section>
    </Card>
  );
}

function Fact({ term, value }: Readonly<{ term: string; value: string }>) {
  return (
    <div className="rounded-md bg-surface-secondary px-3 py-2">
      <dt>
        <Text as="span" variant="caption" tone="tertiary">
          {term}
        </Text>
      </dt>
      <dd>
        <Text as="span" variant="body-sm" weight="semibold">
          {value}
        </Text>
      </dd>
    </div>
  );
}
